import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Calendar,
  CalendarCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Headphones,
  Mail,
  MapPin,
  MessageCircle,
  Mic2,
  Newspaper,
  Play,
  Shield,
  Star,
  Video,
  Zap,
} from "lucide-react";
import {
  BOOK_APPOINTMENT_HREF,
  EXPERT_CTA_LABEL,
  buildProviderCardView,
} from "../../../../utils/providerDisplay";

export const TABS = [
  { id: "overview", label: "Overview", Icon: BookOpen },
  { id: "articles", label: "Articles", Icon: FileText },
  { id: "media", label: "Media", Icon: Newspaper },
];

/** Active on `/legal/*` hubs: page wash & section/tab motifs (`legalHubVisuals.js`). */
export const LegalHubVisualContext = createContext(null);
/** @returns {null | Record<string, unknown>} */
export const useLegalHubVisual = () => useContext(LegalHubVisualContext);

export const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.42, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const tabFade = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.18 } },
};

export const importIcon = (name) => {
  try {
    const mod = require("lucide-react");
    return mod[name] || mod.Star;
  } catch {
    return Zap;
  }
};

/** Readable foreground (#111827 or #fff) on a solid topic accent fill. */
export function foregroundOnSolidAccent(bgHex) {
  const h = String(bgHex ?? "")
    .replace("#", "")
    .slice(0, 6)
    .padStart(6, "f");
  if (!/^[0-9a-f]{6}$/i.test(h)) return "#ffffff";
  const linearize = (pair) => {
    const v = Number.parseInt(pair, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const L =
    0.2126 * linearize(h.slice(0, 2)) +
    0.7152 * linearize(h.slice(2, 4)) +
    0.0722 * linearize(h.slice(4, 6));
  return L > 0.45 ? "#111827" : "#ffffff";
}

/** Large stat numerals on dark hero: use white when the accent is deep (e.g. legal blues). */
export function heroAccentStatColor(bgHex) {
  const h = String(bgHex ?? "")
    .replace("#", "")
    .slice(0, 6)
    .padStart(6, "f");
  if (!/^[0-9a-f]{6}$/i.test(h)) return "#ffffff";
  const linearize = (pair) => {
    const v = Number.parseInt(pair, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const L =
    0.2126 * linearize(h.slice(0, 2)) +
    0.7152 * linearize(h.slice(2, 4)) +
    0.0722 * linearize(h.slice(4, 6));
  return L > 0.55 ? `#${h}` : "#ffffff";
}

const introHeroEase = [0.22, 1, 0.36, 1];

/**
 * Premium intro strip for rich legal topic overviews: meta row + left column (`children`)
 * + photo panel with optional stat chips (from CMS `stats`).
 */
export function LegalTopicIntroHero({
  color,
  gradientClassName,
  categoryEyebrow,
  funnelTagline,
  FunnelIcon,
  gallerySrc,
  galleryImgClassName = "",
  galleryAlt = "",
  imageBandIcon: ImageBandIcon,
  imageBandLabel,
  imageSubtitle,
  statsPreview,
  /** Inset glaze frame + softer vignette — reads well for portrait / editorial photography */
  photoEditorialFrame = false,
  children,
}) {
  const hub = useLegalHubVisual();
  const gradient =
    gradientClassName ??
    (typeof hub?.introGradientTailwind === "string" ? hub.introGradientTailwind : null) ??
    "from-slate-50/75 via-white to-slate-100/55";
  const Fi = FunnelIcon;
  const gridStyle = {
    backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.028) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(15, 23, 42, 0.028) 1px, transparent 1px)`,
    backgroundSize: "28px 28px",
  };
  const preview = Array.isArray(statsPreview) ? statsPreview.slice(0, 3) : [];

  return (
    <motion.section
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-48px" }}
      transition={{ duration: 0.52, ease: introHeroEase }}
      className="relative overflow-hidden rounded-2xl border border-slate-200/95 bg-white shadow-[0_28px_80px_-34px_rgba(15,23,42,0.28)] sm:rounded-[1.95rem]"
    >
      {/* Base wash + readability grid */}
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${gradient}`} aria-hidden />
      <div className="pointer-events-none absolute inset-0" style={gridStyle} aria-hidden />
      <div
        className="pointer-events-none absolute right-[-20%] top-[-30%] h-[26rem] w-[26rem] rounded-full opacity-[0.11] blur-3xl sm:opacity-[0.14]"
        style={{ background: `radial-gradient(circle at center, ${color}, transparent 68%)` }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-300/60 to-transparent"
        aria-hidden
      />

      {/* Accent spine + soft glow */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-1 sm:w-[5px]" aria-hidden>
        <div
          className="absolute inset-y-10 left-0 w-full rounded-full opacity-95"
          style={{
            background: `linear-gradient(180deg, ${color}, ${color}66 42%, transparent)`,
            boxShadow: `6px 0 28px -4px ${color}55`,
          }}
        />
      </div>

      <div className="relative grid lg:grid-cols-12">
        <div className="relative flex flex-col justify-center px-6 py-11 sm:p-10 sm:pb-11 lg:col-span-7 lg:pl-14 lg:pr-8 lg:pb-14 lg:pt-12">
          <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-3">
            <span
              className="inline-flex items-center rounded-full border border-slate-200/90 bg-white/90 px-3.5 py-1 text-[10px] font-black uppercase tracking-[0.28em] text-slate-800 shadow-[0_8px_30px_-20px_rgba(15,23,42,0.35)]"
              style={{ boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.85), 0 0 0 1px ${color}22` }}
            >
              <span className="max-w-[min(100vw-4rem,28rem)] [text-wrap:balance]">{categoryEyebrow}</span>
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline" aria-hidden />
            <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200/95 bg-white/95 px-3.5 py-1.5 text-[11px] font-bold leading-snug text-slate-700 shadow-sm ring-1 ring-slate-200/60">
              {Fi ? <Fi size={14} strokeWidth={2.25} style={{ color }} className="shrink-0" aria-hidden /> : null}
              <span className="min-w-0 [text-wrap:balance]">{funnelTagline}</span>
            </span>
          </div>

          {children}
        </div>

        <div
          className={`relative isolate min-h-[17rem] border-t border-slate-100/90 lg:col-span-5 lg:border-l lg:border-t-0 lg:min-h-[22rem] ${photoEditorialFrame ? "bg-slate-900/5 p-3 sm:p-4 lg:p-5" : ""}`}
        >
          <div
            className={`relative h-full min-h-[15.5rem] overflow-hidden sm:min-h-[17rem] lg:min-h-full ${
              photoEditorialFrame
                ? "rounded-2xl shadow-[0_28px_70px_-40px_rgba(15,23,42,0.55),inset_0_0_0_1px_rgba(255,255,255,0.12)] ring-1 ring-slate-900/10 sm:rounded-[1.35rem] lg:rounded-[1.5rem]"
                : "lg:rounded-r-[calc(1.95rem-1px)]"
            }`}
          >
            <img
              src={gallerySrc}
              alt={galleryAlt}
              className={`absolute inset-0 h-full w-full object-cover ${photoEditorialFrame ? "scale-[1.03] motion-reduce:scale-100" : ""} ${galleryImgClassName}`.trim()}
            />

            {/* Photo depth / crop — editorial frame uses a lighter wash so the subject stays visible */}
            <div
              className={`absolute inset-0 ${
                photoEditorialFrame
                  ? "bg-gradient-to-br from-slate-900/20 via-transparent to-slate-950/30"
                  : "bg-gradient-to-br from-slate-950/[0.12] via-transparent to-slate-950/25"
              } ${photoEditorialFrame ? "rounded-2xl sm:rounded-[1.35rem] lg:rounded-[1.5rem]" : "lg:rounded-r-[calc(1.95rem-1px)]"}`}
              aria-hidden
            />
            <div
              className={`absolute inset-0 ${
                photoEditorialFrame
                  ? "bg-gradient-to-t from-slate-950/88 via-slate-900/28 to-slate-950/[0.08]"
                  : "bg-gradient-to-t from-slate-950/92 via-slate-950/40 to-slate-950/[0.12]"
              } ${photoEditorialFrame ? "rounded-2xl sm:rounded-[1.35rem] lg:rounded-[1.5rem]" : "lg:rounded-r-[calc(1.95rem-1px)]"}`}
              aria-hidden
            />

            {photoEditorialFrame ? (
              <>
                <div
                  className="pointer-events-none absolute inset-x-4 top-4 h-24 bg-gradient-to-b from-white/18 to-transparent blur-2xl sm:inset-x-5 sm:top-5"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute inset-[10px] rounded-[1.15rem] border border-white/22 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)] sm:inset-[12px] sm:rounded-[1.2rem] lg:inset-3.5 lg:rounded-[1.25rem]"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -right-6 bottom-1/3 h-36 w-36 rounded-full opacity-40 blur-3xl"
                  style={{ background: `radial-gradient(circle, ${color}66, transparent 70%)` }}
                  aria-hidden
                />
              </>
            ) : null}

            {preview.length > 0 ? (
              <div
                className={`absolute z-[1] flex max-w-[11.5rem] flex-col gap-2 sm:max-w-[13rem] ${
                  photoEditorialFrame
                    ? "right-4 top-4 sm:right-5 sm:top-5"
                    : "right-4 top-4 sm:right-6 sm:top-6"
                }`}
              >
                {preview.map((s) => (
                  <div
                    key={s.label ?? s.value}
                    className="rounded-xl border border-white/25 bg-black/38 px-3 py-2 text-right shadow-lg backdrop-blur-md"
                    style={{ boxShadow: `0 12px 40px -14px rgba(0,0,0,0.65), inset 0 1px 0 0 rgba(255,255,255,0.12)` }}
                  >
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/72">{String(s.label ?? "").slice(0, 42)}</p>
                    <p className="truncate text-lg font-black leading-none text-white sm:text-xl">{s.value}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {ImageBandIcon || imageSubtitle ? (
              <div className={`absolute inset-x-0 bottom-0 z-[1] ${photoEditorialFrame ? "p-5 pb-6 sm:p-6 sm:pb-8" : "p-6 sm:p-8"}`}>
                {ImageBandIcon ? (
                  <div className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/12 px-3.5 py-2 backdrop-blur-md">
                    <ImageBandIcon size={15} className="shrink-0 text-white/95" strokeWidth={2.25} aria-hidden />
                    <span className="text-[10px] font-black uppercase tracking-[0.24em] text-white/92">{imageBandLabel}</span>
                  </div>
                ) : null}
                {imageSubtitle ? (
                  <p className="mt-3 max-w-[20rem] text-lg font-black leading-snug text-white shadow-black/45 [text-shadow:0_2px_24px_rgba(0,0,0,0.45)] sm:text-xl">
                    {imageSubtitle}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

/** Section headings — legal hubs get per-topic accent motifs via `LegalHubVisualContext`. */
export const SectionHead = ({ eyebrow, title, subtitle, color, light = false }) => {
  const hub = useLegalHubVisual();
  const mode = !light && hub ? hub.sectionAccent ?? "bar" : null;

  const legalAccent = () => {
    switch (mode) {
      case "warm-column":
        return <span className="h-16 w-1 rounded-full bg-gradient-to-b from-rose-400/90 to-rose-200/55 shadow-sm" />;
      case "corporate-double":
        return (
          <div className="flex flex-col gap-2">
            <span className="h-1 w-14 rounded-full" style={{ background: color }} />
            <span className="h-1 w-9 rounded-full bg-slate-300/90" />
          </div>
        );
      case "heritage":
        return (
          <div className="flex flex-col gap-2">
            <span className="h-1 w-[3.25rem] rounded-full" style={{ background: color }} />
            <div className="flex gap-1">
              <span className="h-2 w-2 rounded-sm" style={{ background: color }} />
              <span className="h-2 w-2 rounded-sm bg-amber-400/95" />
            </div>
          </div>
        );
      case "bar-bold":
        return (
          <span
            className="h-[4.75rem] w-[5px] rounded-full"
            style={{
              background: `linear-gradient(180deg, ${color}, ${color}3d)`,
              boxShadow: `0 0 0 1px ${color}22`,
            }}
          />
        );
      case "alert-stripe":
        return (
          <span
            className="h-[3.75rem] w-[6px] rounded-md shadow-sm"
            style={{ background: `linear-gradient(180deg, ${color}, #eab30899, ${color}77)` }}
          />
        );
      case "urgent-dot":
        return (
          <div className="flex flex-col items-center gap-3">
            <span
              className="h-2.5 w-2.5 animate-pulse rounded-full"
              style={{ background: color, boxShadow: `0 0 0 5px ${color}33` }}
            />
            <span className="h-12 w-[3px] rounded-full" style={{ background: color }} />
          </div>
        );
      default:
        return (
          <span
            className="h-[3.25rem] w-[4px] rounded-full sm:h-[4rem]"
            style={{
              background: `linear-gradient(180deg, ${color}, ${color}42)`,
              boxShadow: `0 0 0 1px ${color}1a`,
            }}
          />
        );
    }
  };

  if (light) {
    return (
      <div className="mb-8 md:mb-10">
        <span className="block text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: `${color}cc` }}>
          {eyebrow}
        </span>
        <h2 className="mt-2 text-2xl font-black leading-tight text-white sm:text-3xl">{title}</h2>
        {subtitle ? <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/65">{subtitle}</p> : null}
      </div>
    );
  }

  if (hub) {
    return (
      <div className="mb-9 md:mb-11">
        <div className="flex gap-5 sm:gap-7">
          <div className="flex shrink-0 flex-col items-center pt-1 sm:items-start sm:pt-2" aria-hidden>
            {legalAccent()}
          </div>
          <div className="min-w-0 flex-1">
            <span className="block text-[10px] font-black uppercase tracking-[0.28em] sm:text-[11px] sm:tracking-[0.3em]" style={{ color }}>
              {eyebrow}
            </span>
            <h2 className="mt-2 text-[1.35rem] font-black leading-[1.16] tracking-[-0.02em] text-slate-950 sm:text-[1.75rem] lg:text-[1.95rem]">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-[0.9375rem] sm:leading-relaxed">{subtitle}</p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 md:mb-10">
      <span className="block text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
        {eyebrow}
      </span>
      <h2 className="mt-2 text-2xl font-black leading-tight text-gray-950 sm:text-3xl">{title}</h2>
      {subtitle ? <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500">{subtitle}</p> : null}
      <div className="mt-6 h-[3px] w-16 rounded-full sm:w-20" style={{ background: color }} aria-hidden />
    </div>
  );
};

const faqPanelEase = [0.22, 1, 0.36, 1];

export const FAQItem = ({ q, a, color }) => {
  const [open, setOpen] = useState(false);
  const uid = useId();
  const contentId = `${uid}-faq-a`;
  const btnId = `${uid}-faq-q`;

  return (
    <div
      className="group/faq overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_2px_20px_-12px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/35 transition-[box-shadow,border-color] hover:border-slate-300/95 hover:shadow-[0_8px_32px_-20px_rgba(15,23,42,0.18)]"
      style={open ? { borderColor: `${color}40`, boxShadow: `0 12px 40px -26px rgba(15,23,42,0.15), inset 4px 0 0 0 ${color}` } : undefined}
    >
      <button
        type="button"
        id={btnId}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-[1.125rem] text-left transition-colors duration-200 hover:bg-slate-50/95 sm:py-5"
      >
        <span className="text-sm font-bold leading-snug text-slate-800">{q}</span>
        <ChevronRight
          size={16}
          strokeWidth={2}
          className={`shrink-0 text-slate-400 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? "rotate-90" : "rotate-0"}`}
          style={open ? { color } : undefined}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="panel"
            id={contentId}
            role="region"
            aria-labelledby={btnId}
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                opacity: { duration: 0.16, ease: faqPanelEase },
                height: { duration: 0.36, ease: faqPanelEase, delay: 0.02 },
              },
            }}
            transition={{
              height: { duration: 0.42, ease: faqPanelEase },
              opacity: { duration: 0.28, ease: faqPanelEase, delay: 0.05 },
            }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-100 px-5 pb-5 pt-3.5">
              <p className="text-sm leading-relaxed text-slate-600">{a}</p>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export const ProviderSidebarCard = ({
  industryId,
  provider,
  isAuthenticated,
  fallbackSponsor,
  topicLabel,
  color,
  onTalkToExpertClick,
}) => {
  const view = buildProviderCardView({
    industryId,
    provider,
    isAuthenticated,
    fallbackSponsor,
    topicLabel,
  });

  return (
    <div className="shrink-0 overflow-hidden rounded-[1.375rem] border border-slate-200/90 bg-white shadow-[0_4px_28px_-18px_rgba(15,23,42,0.14)] ring-1 ring-slate-200/35">
      <div
        className="h-1 w-full"
        style={{ background: `linear-gradient(90deg, ${color}, color-mix(in srgb, ${color} 45%, white))` }}
        aria-hidden
      />
      <div className="relative px-4 pb-4 pt-4 sm:px-5 sm:pt-5">
        <div
          className="pointer-events-none absolute -right-8 top-6 h-24 w-24 rounded-full opacity-[0.12] blur-2xl"
          style={{ background: color }}
          aria-hidden
        />
        {view.showVerifiedBadge ? (
          <div
            className="relative mb-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em] shadow-sm"
            style={{
              borderColor: `${color}40`,
              background: `color-mix(in srgb, ${color} 10%, white)`,
              color: "rgb(30 41 59)",
            }}
          >
            <Shield size={11} strokeWidth={2.25} style={{ color }} aria-hidden />
            Verified partner
          </div>
        ) : null}
        <div className="relative flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-base font-black leading-none shadow-[0_6px_18px_-6px_rgba(15,23,42,0.25)] ring-2 ring-white"
            style={{ background: color, color: foregroundOnSolidAccent(color) }}
          >
            {view.initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-black leading-tight text-slate-900">{view.displayName}</p>
            {view.subtitle ? (
              <p className="truncate text-[11px] leading-snug font-semibold text-slate-500">{view.subtitle}</p>
            ) : null}
          </div>
        </div>
        <div className="relative mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-semibold text-slate-500">
          {view.showRating ? (
            <>
              <span className="inline-flex shrink-0 items-center gap-0.5 tabular-nums text-slate-700">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} size={10} fill={s <= 4 ? color : "none"} strokeWidth={1.35} style={{ color }} />
                ))}
                <span className="ml-0.5">4.9</span>
              </span>
              <span className="text-slate-300" aria-hidden>
                ·
              </span>
              <span className="inline-flex items-center gap-1 text-slate-600">
                <CheckCircle2 size={11} strokeWidth={2} style={{ color }} />
                Verified
              </span>
              <span className="text-slate-300" aria-hidden>
                ·
              </span>
            </>
          ) : null}
          {view.showResponseTime && view.responseTime ? (
            <span className="flex min-w-0 items-center gap-1">
              <Clock size={11} strokeWidth={2} className="shrink-0 text-slate-400" />
              <span className="min-w-0 truncate">{view.responseTime}</span>
            </span>
          ) : null}
        </div>
        {view.showTrustLine && view.trustLine ? (
          <p className="relative mt-3 line-clamp-2 rounded-lg border border-slate-100 bg-slate-50/90 px-3 py-2 text-[11px] font-medium leading-snug italic text-slate-600">
            “{view.trustLine}”
          </p>
        ) : null}
        <Link
          to={view.bookAppointmentHref}
          className="relative mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-bold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ outlineColor: color }}
        >
          <CalendarCheck size={13} strokeWidth={2.25} aria-hidden />
          {view.bookAppointmentLabel}
        </Link>
        {onTalkToExpertClick ? (
          <button
            type="button"
            onClick={onTalkToExpertClick}
            className="relative mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-[11px] font-bold text-slate-600 transition hover:text-slate-900"
          >
            <MessageCircle size={13} strokeWidth={2.25} style={{ color }} aria-hidden />
            Talk to an expert
          </button>
        ) : view.whatsappUrl ? (
          <a
            href={view.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-[11px] font-bold text-slate-600 transition hover:text-slate-900"
          >
            <MessageCircle size={13} strokeWidth={2.25} style={{ color }} aria-hidden />
            Talk to an expert
          </a>
        ) : null}
        {view.email ? (
          <a
            href={`mailto:${view.email}`}
            className="relative mt-2 flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/90 px-3 py-2 text-[11px] font-bold text-slate-700 transition hover:border-slate-200 hover:bg-slate-100"
          >
            <Mail size={13} strokeWidth={2.25} style={{ color }} aria-hidden />
            {view.email}
          </a>
        ) : null}
      </div>

      {view.showConsultant && view.consultant ? (
        <div className="flex items-center gap-2.5 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-5">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-black shadow-sm ring-2 ring-white"
            style={{ background: color, color: foregroundOnSolidAccent(color) }}
          >
            {view.consultant
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-bold text-slate-900">{view.consultant}</p>
            <p className="truncate text-[10px] font-semibold text-slate-500">Consultant</p>
          </div>
        </div>
      ) : null}

    </div>
  );
};

export const ProviderHeroCard = ({
  industryId,
  provider,
  isAuthenticated,
  fallbackSponsor,
  topicLabel,
  color,
  onTalkToExpertClick,
}) => {
  const view = buildProviderCardView({
    industryId,
    provider,
    isAuthenticated,
    fallbackSponsor,
    topicLabel,
  });

  return (
    <div className="rounded-[1.75rem] border border-white/18 bg-white/[0.06] p-5 shadow-[0_28px_60px_-32px_rgba(0,0,0,0.55)] backdrop-blur-md ring-1 ring-white/10">
      <div className="flex items-center gap-4">
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-lg font-black shadow-lg"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          {view.initials}
        </div>
        <div>
          <p className="font-black text-white">{view.displayName}</p>
          {view.subtitle ? <p className="text-xs text-white/50">{view.subtitle}</p> : null}
          {view.showRating ? (
            <div className="mt-1 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={10} fill={s <= 4 ? color : "none"} style={{ color }} strokeWidth={1.5} />
              ))}
              <span className="ml-1 text-[10px] text-white/40">4.9</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        {view.showResponseTime && view.responseTime ? (
          <div className="flex items-center gap-2 text-[11px] text-white/50">
            <Clock size={11} style={{ color }} />
            {view.responseTime}
          </div>
        ) : null}
        {view.showTrustLine && view.trustLine ? (
          <p className="text-[11px] leading-relaxed italic text-white/45">“{view.trustLine}”</p>
        ) : null}
      </div>
      <Link
        to={view.bookAppointmentHref}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 py-3 text-xs font-black text-white backdrop-blur transition hover:bg-white/15"
      >
        <CalendarCheck size={12} strokeWidth={2.25} aria-hidden />
        {view.bookAppointmentLabel}
        <ArrowRight size={12} aria-hidden />
      </Link>
      {onTalkToExpertClick ? (
        <button
          type="button"
          onClick={onTalkToExpertClick}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-[11px] font-bold text-white/75 transition hover:text-white"
        >
          <MessageCircle size={12} strokeWidth={2.25} style={{ color }} aria-hidden />
          Talk to an expert
        </button>
      ) : view.whatsappUrl ? (
        <a
          href={view.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-[11px] font-bold text-white/75 transition hover:text-white"
        >
          <MessageCircle size={12} strokeWidth={2.25} style={{ color }} aria-hidden />
          Talk to an expert
        </a>
      ) : null}
    </div>
  );
};

/** Dark sidebar CTA — book appointment under trust line; Talk to an expert (gated). */
export const ExpertProviderCta = ({
  whatsappUrl,
  trustLine,
  responseTime,
  bookAppointmentHref = BOOK_APPOINTMENT_HREF,
  bookAppointmentLabel = "Book appointment",
  onTalkToExpertClick,
  className = "",
}) => (
  <div className={`rounded-[2rem] bg-black p-6 text-white shadow-xl ${className}`}>
    <p className="text-[11px] font-black uppercase tracking-wider text-[#FFD221]">Provider CTA</p>
    <h3 className="mt-3 text-xl font-black">{EXPERT_CTA_LABEL}</h3>
    {responseTime ? (
      <p className="mt-2 flex items-center gap-2 text-sm text-white/55">
        <Clock size={14} className="shrink-0 text-[#FFD221]" aria-hidden />
        {responseTime}
      </p>
    ) : null}
    {trustLine ? (
      <p className="mt-2 text-sm leading-relaxed italic text-white/60">“{trustLine}”</p>
    ) : null}
    <Link
      to={bookAppointmentHref}
      className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFD221] px-4 py-3 text-sm font-black text-black transition hover:brightness-105"
    >
      <CalendarCheck size={15} strokeWidth={2.25} aria-hidden />
      {bookAppointmentLabel}
    </Link>
    {whatsappUrl ? (
      onTalkToExpertClick ? (
        <button
          type="button"
          onClick={onTalkToExpertClick}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-3 text-sm font-bold text-white transition hover:border-white/30 hover:bg-white/5"
        >
          <MessageCircle size={15} aria-hidden />
          Talk to an expert
        </button>
      ) : (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-3 text-sm font-bold text-white transition hover:border-white/30 hover:bg-white/5"
        >
          <MessageCircle size={15} aria-hidden />
          Talk to an expert
        </a>
      )
    ) : (
      <p className="mt-2 text-center text-sm text-white/50">WhatsApp contact is not available for this topic yet.</p>
    )}
  </div>
);

export const ArticleCard = ({ article, to, color, gated = false }) => (
  <Link
    to={to}
    className="group relative flex flex-col overflow-hidden rounded-[1.35rem] border border-slate-200/90 bg-white shadow-[0_4px_24px_-16px_rgba(15,23,42,0.14)] ring-1 ring-slate-200/30 transition-all duration-300 hover:-translate-y-1.5 hover:border-slate-300/95 hover:shadow-[0_22px_50px_-28px_rgba(15,23,42,0.22)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
    style={{ outlineColor: color }}
    aria-label={gated ? `${article.title} — open preview` : article.title}
  >
    <span
      className="pointer-events-none absolute inset-y-0 left-0 w-[3px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      style={{ background: color }}
      aria-hidden
    />
    <div className="relative h-48 overflow-hidden bg-slate-100">
      <img
        src={article.coverImage}
        alt=""
        className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.06]"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />
      <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5">
        <span
          className="rounded-lg px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] shadow-md"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          {article.type}
        </span>
        <span className="rounded-lg border border-white/25 bg-white/92 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-900 shadow-sm backdrop-blur-sm">
          {article.readTime}
        </span>
      </div>
    </div>
    <div className="relative flex flex-1 flex-col p-5 sm:p-6">
      {gated ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center rounded-b-[1.35rem] bg-white/70 px-4 backdrop-blur-[3px]">
          <span className="rounded-full bg-[#FFD221] px-3 py-1 text-[10px] font-black uppercase tracking-wider text-black">
            Open preview
          </span>
        </div>
      ) : null}
      <h3
        className={`flex-1 text-[1.05rem] font-black leading-snug tracking-tight text-slate-950 transition group-hover:text-slate-800 ${gated ? "select-none blur-[4px]" : ""}`}
      >
        {article.title}
      </h3>
      <p
        className={`mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600 ${gated ? "select-none blur-[4px]" : ""}`}
      >
        {article.excerpt}
      </p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {article.tags?.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="rounded-lg border border-slate-200/80 bg-slate-50/90 px-2 py-0.5 text-[10px] font-bold text-slate-700 transition group-hover:border-slate-300"
            style={{ borderColor: `${color}22`, color }}
          >
            {tag}
          </span>
        ))}
      </div>
      <span className="mt-5 inline-flex items-center gap-2 text-xs font-black text-slate-900">
        {gated ? "Preview article" : "Read article"}
        <ArrowRight size={13} strokeWidth={2.5} className="transition group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

export const VideoCard = ({ video, color }) => (
  <div className="group relative flex flex-col overflow-hidden rounded-[1.625rem] border border-black/[0.06] bg-white shadow-[0_14px_40px_-32px_rgba(0,0,0,0.35)] ring-1 ring-black/[0.02] transition-[box-shadow,border-color,transform] duration-300 ease-out hover:-translate-y-[2px] hover:border-black/[0.1] hover:shadow-[0_22px_56px_-36px_rgba(0,0,0,0.38)]">
    <div className="relative isolate aspect-video w-full overflow-hidden bg-neutral-950">
      <img
        src={video.poster}
        alt=""
        className="absolute inset-0 h-full w-full object-cover brightness-[0.92] saturate-[1.05] transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.07] group-hover:brightness-[0.82]"
        loading="lazy"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_100%_70%_at_50%_0%,transparent_25%,rgba(0,0,0,0.45))]"
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/[0.88] via-black/35 to-black/55" aria-hidden />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: `linear-gradient(125deg, transparent 42%, ${color}40 118%)` }}
        aria-hidden
      />

      <div className="absolute left-3 top-3 z-10 flex items-start gap-2 sm:left-4 sm:top-4">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/22 bg-black/42 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-white shadow-[0_8px_24px_-14px_rgba(0,0,0,0.45)] backdrop-blur-md">
          <Video size={12} strokeWidth={2.25} style={{ color }} aria-hidden />
          Video
        </span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 px-3 pb-[13px] pt-14 sm:px-4 sm:pb-4">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <p className="line-clamp-2 min-w-0 flex-1 text-left text-[13px] font-black leading-snug tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)] sm:text-sm">
            {video.title}
          </p>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-white/16 bg-black/48 px-2.5 py-1 text-[10px] font-black tabular-nums text-white backdrop-blur-md">
            <Clock size={11} strokeWidth={2} className="opacity-85" aria-hidden />
            {video.duration}
          </span>
        </div>
      </div>

      <button
        type="button"
        className="absolute left-1/2 top-[44%] z-10 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/55 bg-white text-neutral-950 shadow-[0_12px_40px_-14px_rgba(0,0,0,0.55)] ring-[3px] ring-black/12 transition-[transform,box-shadow,border-color] duration-300 ease-out hover:border-white hover:shadow-[0_18px_50px_-16px_rgba(0,0,0,0.65)] group-hover:-translate-x-1/2 group-hover:-translate-y-1/2 group-hover:scale-[1.08] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:h-[3.75rem] sm:w-[3.75rem]"
        style={{ outlineColor: color, boxShadow: `0 0 0 1px ${color}55, 0 16px 48px -18px rgba(0,0,0,0.55)` }}
        aria-label={`Play video: ${video.title}`}
      >
        <Play size={22} strokeWidth={0} fill="currentColor" className="ml-0.5 sm:h-6 sm:w-6" />
      </button>

      <span
        className="pointer-events-none absolute bottom-0 left-0 right-0 z-[5] block h-[3px]"
        style={{ background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`, opacity: 0.92 }}
        aria-hidden
      />
    </div>

    <div className="relative flex flex-1 flex-col border-t border-black/[0.05] px-4 pb-[1.125rem] pt-4 sm:px-[1.125rem] sm:pb-5 sm:pt-[1.125rem]">
      <h3 className="text-base font-black leading-snug tracking-tight text-gray-950">{video.title}</h3>
      <p className="mt-2 text-xs leading-relaxed text-gray-500 sm:text-[13px] sm:leading-[1.65]">{video.description}</p>
      <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.12em]" style={{ color }}>
        Tap to preview
        <ArrowRight size={12} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1" aria-hidden />
      </span>
    </div>
  </div>
);

/** Subpixel / snap tolerance for prev/next disabling. */
const SCROLL_EDGE_EPS = 12;

/** Left edge of slide within scroller coordinate space — reliable regardless of offsetParent chains. */
const slideLeftWithinScroller = (scrollerEl, slideEl) => {
  const sBox = scrollerEl.getBoundingClientRect();
  return slideEl.getBoundingClientRect().left - sBox.left + scrollerEl.scrollLeft;
};

/**
 * Horizontal carousel: scroll-snap, prev/next controls, touch swipe.
 */
export const MediaCarousel = ({
  items,
  renderItem,
  variant,
  color,
  ariaLabel,
  header,
  meta,
}) => {
  const scrollerRef = useRef(null);
  const scrollerId = useId();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    setCanPrev(scrollLeft > SCROLL_EDGE_EPS);
    const atEnd =
      maxScroll <= SCROLL_EDGE_EPS || scrollLeft >= maxScroll - SCROLL_EDGE_EPS;
    setCanNext(!atEnd && maxScroll > SCROLL_EDGE_EPS);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return undefined;

    let rafQueued = false;
    const scheduleArrowUpdate = () => {
      if (rafQueued) return;
      rafQueued = true;
      requestAnimationFrame(() => {
        rafQueued = false;
        updateArrows();
      });
    };

    el.addEventListener("scroll", updateArrows, { passive: true });
    const ro = new ResizeObserver(scheduleArrowUpdate);
    ro.observe(el);
    window.addEventListener("resize", updateArrows);

    const imgCleanups = [];
    requestAnimationFrame(() => {
      [...el.querySelectorAll("img")].forEach((img) => {
        if (img.complete) return;
        const h = () => scheduleArrowUpdate();
        img.addEventListener("load", h, { once: true });
        imgCleanups.push(() => img.removeEventListener("load", h));
      });
    });

    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
      window.removeEventListener("resize", updateArrows);
      imgCleanups.forEach((fn) => fn());
    };
  }, [updateArrows, items?.length]);

  /** Move by one discrete slide anchor (snap-friendly). */
  const scrollToAdjacentSlide = useCallback(
    (dir) => {
      const el = scrollerRef.current;
      if (!el) return;
      const slides = [...el.querySelectorAll("[data-carousel-slide]")];
      if (slides.length === 0) return;

      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      const positions = slides.map((slide) => slideLeftWithinScroller(el, slide));
      const viewportStart = el.scrollLeft;

      let targetLeft;

      if (dir > 0) {
        const idx = positions.findIndex((p) => p > viewportStart + SCROLL_EDGE_EPS);
        targetLeft = idx === -1 ? maxScroll : Math.min(positions[idx], maxScroll);
      } else {
        let bestLeft = 0;
        for (let i = positions.length - 1; i >= 0; i -= 1) {
          if (positions[i] < viewportStart - SCROLL_EDGE_EPS) {
            bestLeft = positions[i];
            break;
          }
        }
        targetLeft = bestLeft;
      }

      targetLeft = Math.max(0, Math.min(maxScroll, targetLeft));

      el.scrollTo({ left: targetLeft, behavior: "smooth" });

      window.setTimeout(updateArrows, 350);
      window.requestAnimationFrame(() => window.requestAnimationFrame(updateArrows));
    },
    [updateArrows]
  );

  const onRegionKeyDown = (e) => {
    const el = scrollerRef.current;
    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    const isField = tag === "input" || tag === "textarea" || tag === "select" || target?.isContentEditable;
    if (isField) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollToAdjacentSlide(1);
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollToAdjacentSlide(-1);
    }
    if (e.key === "Home") {
      if (!el) return;
      e.preventDefault();
      el.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    if (e.key === "End") {
      if (!el) return;
      e.preventDefault();
      el.scrollTo({ left: Math.max(0, el.scrollWidth - el.clientWidth), behavior: "smooth" });
    }
  };

  if (!items?.length) return null;

  const slideClass =
    variant === "podcast"
      ? [
          "snap-start shrink-0",
          "flex-[0_0_min(19rem,calc(100vw-2rem))]",
          "sm:flex-[0_0_calc((100%-1rem)/2)]",
          "lg:flex-[0_0_calc((100%-2rem)/3)]",
        ].join(" ")
      : [
          "snap-start shrink-0",
          "flex-[0_0_min(21rem,calc(100vw-2rem))]",
          "sm:flex-[0_0_calc((100%-1rem)/2)]",
          "lg:flex-[0_0_calc((100%-1rem)/2)]",
        ].join(" ");

  const navBtnClass =
    "inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/[0.08] bg-white text-gray-800 shadow-sm transition hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-35 sm:h-[2.75rem] sm:w-[2.75rem] md:h-11 md:w-11";

  const showHeadingRow = header != null || meta != null;

  return (
    <div className="w-full min-w-0 space-y-4 md:space-y-5">
      {showHeadingRow ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          {header ? <div className="min-w-0 flex-1">{header}</div> : <div />}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:flex-nowrap sm:justify-end sm:gap-3 md:gap-4">
            {meta != null ? <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 sm:text-[12px]">{meta}</div> : null}
            <div className="hidden shrink-0 items-center gap-1 rounded-full border border-black/[0.08] bg-white p-1 shadow-[0_2px_12px_-8px_rgba(0,0,0,0.14)] sm:inline-flex sm:gap-1.5 sm:p-1.5">
              <button
                type="button"
                className={navBtnClass}
                style={{ outlineColor: color }}
                aria-label="Previous slides"
                aria-controls={scrollerId}
                disabled={!canPrev}
                onClick={() => scrollToAdjacentSlide(-1)}
              >
                <ChevronLeft size={18} strokeWidth={2.25} />
              </button>
              <button
                type="button"
                className={navBtnClass}
                style={{ outlineColor: color }}
                aria-label="Next slides"
                aria-controls={scrollerId}
                disabled={!canNext}
                onClick={() => scrollToAdjacentSlide(1)}
              >
                <ChevronRight size={18} strokeWidth={2.25} />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="relative w-full min-w-0 sm:overflow-hidden sm:rounded-xl sm:bg-[#faf8f2]/50 sm:p-3 sm:ring-1 sm:ring-black/[0.04] md:p-4 lg:p-5">
        <div
          className="relative -mx-1 min-w-0 sm:mx-0"
          role="region"
          aria-roledescription="carousel"
          aria-label={ariaLabel}
          tabIndex={0}
          onKeyDown={onRegionKeyDown}
        >
          <div
            className={`pointer-events-none absolute inset-y-2 left-1 z-[2] hidden w-9 rounded-l-xl bg-gradient-to-r from-[#f7f4ec] to-transparent md:block md:w-10 lg:w-12 ${
              canPrev ? "opacity-95" : "pointer-events-none opacity-0"
            }`}
            aria-hidden
          />
          <div
            className={`pointer-events-none absolute inset-y-2 right-1 z-[2] hidden w-9 rounded-r-xl bg-gradient-to-l from-[#f7f4ec] to-transparent md:block md:w-10 lg:w-12 ${
              canNext ? "opacity-95" : "pointer-events-none opacity-0"
            }`}
            aria-hidden
          />
          {/* Mobile fades */}
          <div
            className={`pointer-events-none absolute inset-y-0 left-0 z-[2] w-7 bg-gradient-to-r from-[#f7f4ec] to-transparent md:hidden ${
              canPrev ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden
          />
          <div
            className={`pointer-events-none absolute inset-y-0 right-0 z-[2] w-7 bg-gradient-to-l from-[#f7f4ec] to-transparent md:hidden ${canNext ? "opacity-100" : "opacity-0"}`}
            aria-hidden
          />

          <div
            ref={scrollerRef}
            id={scrollerId}
            tabIndex={-1}
            className="-webkit-overflow-scrolling-touch relative z-[1] flex touch-pan-x gap-4 overflow-x-auto overscroll-x-contain scroll-smooth scroll-pl-4 scroll-pr-4 px-0 pb-1 pt-1 [scrollbar-width:none] sm:scroll-pl-3 sm:scroll-pr-3 sm:px-0 md:gap-5 md:scroll-pl-2 md:scroll-pr-2 lg:gap-6 [&::-webkit-scrollbar]:hidden"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {items.map((item, idx) => (
              <div
                key={item.id ?? idx}
                data-carousel-slide
                className={slideClass}
                style={{ scrollSnapAlign: "start", scrollSnapStop: "always" }}
              >
                {renderItem(item, idx)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Phone */}
      <div className="flex items-center justify-center gap-3 sm:hidden">
        <button
          type="button"
          className={navBtnClass}
          style={{ outlineColor: color }}
          aria-label="Previous"
          aria-controls={scrollerId}
          disabled={!canPrev}
          onClick={() => scrollToAdjacentSlide(-1)}
        >
          <ChevronLeft size={20} strokeWidth={2} />
        </button>
        <button
          type="button"
          className={navBtnClass}
          style={{ outlineColor: color }}
          aria-label="Next"
          aria-controls={scrollerId}
          disabled={!canNext}
          onClick={() => scrollToAdjacentSlide(1)}
        >
          <ChevronRight size={20} strokeWidth={2} />
        </button>
      </div>
      <p className="mx-auto mt-3 max-w-md text-center text-xs leading-relaxed text-gray-400 sm:hidden">
        Swipe the row to browse — or use the arrows.
      </p>
    </div>
  );
};

export const PodcastCard = ({ podcast, color }) => (
  <div className="group flex h-full min-w-0 flex-col overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-sm transition-shadow duration-300 hover:shadow-[0_16px_40px_-32px_rgba(0,0,0,0.18)]">
    <div className="relative aspect-video w-full overflow-hidden bg-neutral-950">
      <img
        src={podcast.coverImage}
        alt=""
        className="h-full w-full object-cover opacity-85 transition duration-500 group-hover:scale-105"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
      {podcast.listenHref ? (
        <a
          href={podcast.listenHref}
          target="_blank"
          rel="noreferrer"
          className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-black shadow-2xl transition group-hover:scale-110"
          aria-label={`Listen to ${podcast.title}`}
        >
          <Headphones size={20} strokeWidth={2.25} />
        </a>
      ) : (
        <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-black shadow-xl">
          <Headphones size={20} strokeWidth={2.25} />
        </span>
      )}
      <span className="absolute bottom-3 left-3 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-black text-white">
        {podcast.episodeLabel}
      </span>
      <span className="absolute bottom-3 right-3 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-black text-white">{podcast.duration}</span>
    </div>
    <div className="flex flex-1 flex-col p-4">
      <div className="mb-2 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
        <Mic2 size={11} />
        Podcast
      </div>
      <h3 className="text-sm font-black leading-snug text-gray-950 sm:text-[15px]">{podcast.title}</h3>
      <p className="mt-1 text-[11px] font-semibold text-gray-400 sm:text-xs">
        {podcast.host}
        {podcast.publishedAt ? <span className="text-gray-300"> · {podcast.publishedAt}</span> : null}
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-gray-500 sm:text-[13px] sm:leading-relaxed">{podcast.description}</p>
    </div>
  </div>
);

export const EventCard = ({ event, color, ctaTo }) => {
  const pct = Math.round((event.registeredSeats / event.totalSeats) * 100);
  const typeColors = {
    Workshop: { bg: "#f0fdf4", text: "#15803d", dot: "#16a34a" },
    Webinar: { bg: "#eff6ff", text: "#1d4ed8", dot: "#2563eb" },
    Consultation: { bg: "#fefce8", text: "#a16207", dot: "#ca8a04" },
  };
  const tc = typeColors[event.type] || typeColors.Workshop;

  return (
    <div className="overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-sm transition hover:shadow-lg">
      <div className="relative h-44 overflow-hidden bg-gray-100">
        <img src={event.coverImage} alt="" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
        <div className="absolute left-3 top-3 flex gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black" style={{ background: tc.bg, color: tc.text }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: tc.dot }} />
            {event.type}
          </span>
          {event.isOnline ? (
            <span className="rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-black text-gray-800">Online</span>
          ) : null}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-sm font-black leading-snug text-white">{event.title}</p>
        </div>
      </div>
      <div className="p-5">
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar size={13} style={{ color }} />
            {event.date}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Clock size={13} style={{ color }} />
            {event.time}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <MapPin size={13} style={{ color }} />
            <span className="truncate">{event.location}</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
            <Zap size={13} style={{ color }} />
            {event.price}
          </div>
        </div>
        <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-gray-500">{event.description}</p>
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-[10px] font-bold text-gray-400">
            <span>{event.registeredSeats} registered</span>
            <span>{event.totalSeats - event.registeredSeats} seats left</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-400">
            Hosted by <span className="font-bold text-gray-700">{event.host}</span>
          </p>
          <Link
            to={ctaTo}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-black transition hover:brightness-90"
            style={{ background: color, color: foregroundOnSolidAccent(color) }}
          >
            Register
            <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export const StickyTabBar = ({ activeTab, setActiveTab, color, articleCount, mediaCount, hubVisual }) => {
  const counts = { overview: null, articles: articleCount, media: mediaCount };
  const variant = hubVisual?.stickyTabVariant;

  if (variant === "docked" || variant === "docked-soft") {
    const soft = variant === "docked-soft";
    return (
      <div
        className="sticky top-16 z-[90] border-b border-slate-200/65 bg-white/[0.92] shadow-[0_12px_46px_-28px_rgba(15,23,42,0.2)] backdrop-blur-xl supports-[backdrop-filter]:bg-white/70 sm:top-[70px]"
        role="navigation"
        aria-label="Section tabs"
      >
        <div className="mx-auto max-w-8xl px-4 py-3.5 sm:px-6 lg:px-8">
          <div
            className={`flex w-full max-w-2xl items-center gap-1 rounded-[1.125rem] border p-1.5 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] sm:max-w-none sm:gap-1.5 lg:inline-flex lg:w-auto ${
              soft
                ? "border-rose-900/[0.06] bg-gradient-to-b from-white to-rose-50/30"
                : "border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-slate-100/50"
            }`}
          >
            {TABS.map(({ id, label, Icon }) => {
              const isActive = activeTab === id;
              const cnt = counts[id];
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`relative flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-xs font-bold transition-all sm:flex-initial sm:gap-2.5 sm:px-5 sm:py-3 sm:text-[0.9375rem] ${
                    isActive
                      ? "bg-white text-slate-900 shadow-[0_10px_28px_-18px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/90"
                      : "text-slate-500 hover:bg-white/70 hover:text-slate-900"
                  }`}
                >
                  <Icon size={16} strokeWidth={2.25} className="shrink-0 opacity-90" style={isActive ? { color } : undefined} />
                  <span className="truncate">{label}</span>
                  {cnt ? (
                    <span
                      className="ml-0.5 hidden min-w-[1.5rem] rounded-lg px-1.5 py-0.5 text-center text-[10px] font-black tabular-nums sm:inline-block"
                      style={{
                        background: isActive ? `${color}20` : "rgba(15,23,42,0.06)",
                        color: isActive ? color : "#94a3b8",
                      }}
                    >
                      {cnt}
                    </span>
                  ) : null}
                  {isActive ? (
                    <span
                      className="pointer-events-none absolute bottom-1 left-1/2 h-0.5 w-[38%] max-w-[3.5rem] -translate-x-1/2 rounded-full sm:bottom-1.5"
                      style={{ background: color }}
                      aria-hidden
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="sticky top-16 z-[90] border-b border-slate-200/80 bg-white/95 shadow-[0_10px_40px_-24px_rgba(15,23,42,0.15)] backdrop-blur-xl sm:top-[70px]"
      role="navigation"
      aria-label="Section tabs"
    >
      <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
        <div className="scrollbar-none flex items-center gap-0.5 overflow-x-auto sm:gap-1">
          {TABS.map(({ id, label, Icon }) => {
            const isActive = activeTab === id;
            const cnt = counts[id];
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`relative flex shrink-0 items-center gap-2 border-b-[3px] px-4 py-3.5 text-sm font-bold transition-colors sm:px-5 sm:py-4 ${
                  isActive ? "border-current" : "border-transparent text-slate-500 hover:bg-slate-50/90 hover:text-slate-900"
                }`}
                style={isActive ? { color, borderColor: color } : undefined}
              >
                <Icon size={15} strokeWidth={2.25} />
                {label}
                {cnt ? (
                  <span
                    className="rounded-lg px-1.5 py-0.5 text-[10px] font-black"
                    style={{
                      background: isActive ? `${color}20` : "#f1f5f9",
                      color: isActive ? color : "#94a3b8",
                    }}
                  >
                    {cnt}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const GenericOverviewTab = ({ experience, summary, color }) => (
  <div className="space-y-8">
    {summary ? (
      <section className="overflow-hidden rounded-[2rem] border border-black/[0.08] bg-white shadow-sm">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative min-h-[16rem]">
            <img src={experience.gallery[1]} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <p className="text-xs font-black uppercase tracking-[0.2em]" style={{ color }}>
                Overview
              </p>
              <h2 className="mt-1 text-xl font-black text-white">What consumers should know</h2>
            </div>
          </div>
          <div className="p-7">
            <p className="text-sm leading-8 text-gray-600">{summary.intro}</p>
            {summary.types?.length > 0 ? (
              <div className="mt-5">
                <p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Types</p>
                <div className="flex flex-wrap gap-2">
                  {summary.types.map((t) => (
                    <span key={t} className="rounded-full px-3 py-1 text-xs font-bold text-black" style={{ background: `${color}18` }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {summary.lookout?.length > 0 ? (
              <div className="mt-5 space-y-2">
                {summary.lookout.map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color }} />
                    {item}
                  </div>
                ))}
              </div>
            ) : null}
            {summary.tip ? (
              <div className="mt-5 rounded-2xl border-l-4 p-4" style={{ borderColor: color, background: `${color}10` }}>
                <p className="text-xs font-black uppercase tracking-[0.18em]" style={{ color }}>
                  Expert tip
                </p>
                <p className="mt-1 text-sm font-bold leading-relaxed text-gray-800">{summary.tip}</p>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    ) : null}
    <div className="grid gap-4 md:grid-cols-3">
      {experience.modules.map((module, idx) => (
        <div key={module.title} className="rounded-3xl border border-black/[0.08] bg-white p-6 shadow-sm">
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-2xl" style={{ background: `${color}18`, color }}>
            {React.createElement([BookOpen, FileText, Zap][idx], { size: 18 })}
          </div>
          <h3 className="text-base font-black text-gray-950">{module.title}</h3>
          <p className="mt-2 text-sm leading-7 text-gray-600">{module.text}</p>
        </div>
      ))}
    </div>
  </div>
);

export const GenericArticlesTab = ({ articles, color, industryId, slug, experience }) => (
  <div className="space-y-8">
    <div className="grid gap-5 md:grid-cols-3">
      {articles.map((article) => (
        <ArticleCard
          key={article.id}
          article={article}
          to={`/${industryId}/${slug}/${article.slug}`}
          color={color}
        />
      ))}
    </div>
    <div className="overflow-hidden rounded-[2rem] bg-gray-950 text-white">
      <div className="relative flex min-h-[20rem] flex-col justify-end p-7">
        <img src={experience.video.poster} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <button
          type="button"
          className="relative mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#FFD221] text-black shadow-2xl"
          aria-label="Play"
        >
          <Play size={22} fill="currentColor" />
        </button>
        <p className="relative text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
          Video placeholder
        </p>
        <h2 className="relative mt-1 text-xl font-black">{experience.video.title}</h2>
        <p className="relative mt-1 text-sm text-white/50">{experience.video.duration} provider intro</p>
      </div>
    </div>
  </div>
);

export const GenericMediaTab = ({ color, ctaTo }) => (
  <div className="flex min-h-[24rem] flex-col items-center justify-center rounded-[2rem] border border-dashed border-black/20 bg-white px-8 py-16 text-center">
    <div className="mb-5 flex items-center gap-3">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: `${color}18`, color }}>
        <Video size={26} strokeWidth={2} />
      </div>
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: `${color}18`, color }}>
        <Mic2 size={26} strokeWidth={2} />
      </div>
    </div>
    <h3 className="text-xl font-black text-gray-900">Videos &amp; podcasts coming soon</h3>
    <p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
      Curated clips and episodes for this topic will appear here. Register to get notified when guides, listen links, and new drops go live.
    </p>
    <Link
      to={ctaTo}
      className="mt-7 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
      style={{ background: color, color: foregroundOnSolidAccent(color) }}
    >
      Register &amp; Get Notified
      <ArrowRight size={14} />
    </Link>
  </div>
);
